/**
 * StringListEditor — Reusable string array editor.
 * Used for learned patterns, code conventions, and common failures.
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';

interface StringListEditorProps {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxItems?: number;
}

export function StringListEditor({
  items,
  onChange,
  placeholder = 'Add item...',
  disabled,
  maxItems = 20,
}: StringListEditorProps) {
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed || items.includes(trimmed) || items.length >= maxItems) return;
    onChange([...items, trimmed]);
    setNewItem('');
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="space-y-1.5">
      {/* Existing items */}
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-2 group"
            >
              <div className="flex-1 px-3 py-1.5 bg-neutral-50 border border-neutral-200 rounded-lg text-[12px] text-neutral-700">
                {item}
              </div>
              {!disabled && (
                <button
                  onClick={() => removeItem(index)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new item */}
      {!disabled && items.length < maxItems && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 px-3 py-1.5 border border-neutral-200 rounded-lg text-[12px] text-neutral-700 focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none"
          />
          <button
            onClick={addItem}
            disabled={!newItem.trim()}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors disabled:opacity-40"
          >
            <Plus size={12} />
            Add
          </button>
        </div>
      )}

      {items.length === 0 && disabled && (
        <div className="text-[11px] text-neutral-400 py-2">No items configured</div>
      )}
    </div>
  );
}
